/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { HeaderSize } from "@tps/Overrides/Pages/Header";
import { xScreen } from "@tps/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface LogoLinksSearchStyles {
    logoWrapper: InjectableCss;
    searchWrapper?: InjectableCss;
    containerContentWrapper?: InjectableCss;
    navigationWrapper?: InjectableCss;
    mainNavigationWrapper?: InjectableCss;
    menuTriggerButton?: ClickablePresentationProps;
    menuIcon?: IconPresentationProps;
    searchMenuWrapper?: InjectableCss;
    accountMenuWrapper?: InjectableCss;
    cartLinkWrapper?: InjectableCss;
    accountCartWrapper?: InjectableCss;
    mobileSearchWrapper?: InjectableCss;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    containerContentWrapper: {
        css: css`
            position: relative;
            display: flex;
            justify-content: space-between;
            align-items: center;
            height: 66px;
            ${xScreen("lte", "md")} {
                height: 56px;
            }

            ${({ isCheckoutPages }: any) =>
                isCheckoutPages &&
                css`
                    height: ${HeaderSize.checkoutHeader}px;
                    justify-content: flex-start;
                `}
        `,
    },
    logoWrapper: {
        css: css`
            height: 42px;
            min-width: 146px;
            margin-right: 40px;
            margin-left: 0;
            display: flex;
            align-items: center;
            width: 160px;

            ${({ isCheckoutPages }: any) =>
                isCheckoutPages
                    ? css`
                          height: 40px;
                          width: 154px;
                      `
                    : css`
                          ${xScreen("lte", "md")} {
                              height: 40px;
                              width: 154px;
                              margin-inline: 24px;
                              ${({ searchInputFocus }: any) =>
                                  searchInputFocus &&
                                  `width:0;max-width:0;height:0;visibility:hidden;min-width:0;margin:0`};
                          }
                          ${xScreen("lte", 767)} {
                              position: absolute;
                              left: 50%;
                              top: 50%;
                              transform: translate3d(-50%, -50%, 0);
                              margin: 0;
                          }
                      `}
        `,
    },
    menuTriggerButton: {
        css: css`
            ${xScreen("lte", "md")} {
                ${({ searchInputFocus }: any) => searchInputFocus && `display:none;`};
            }
            width: 32px;
            height: 32px;
            justify-content: center;
        `,
    },
    navigationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex: 1;
        `,
    },
    mainNavigationWrapper: {
        css: css`
            width: 80px;
            margin-right: 40px;

            ${xScreen("lte", "sm")} {
                display: none;
            }
        `,
    },
    searchWrapper: {
        css: css`
            max-width: 743px;
            flex: 1 1 auto;
            margin-right: 32px;

            ${xScreen("lte", "md")} {
                margin-right: 24px;
                ${({ searchInputFocus }: any) => searchInputFocus && `width:100%;max-width:100%;margin-right:48px;`};
            }

            ${({ searchInputFocus }: any) => !searchInputFocus && `display: none;`}
            ${xScreen("gte", "md")} {
                display: block;
            }
        `,
    },
    mobileSearchWrapper: {
        css: css`
            ${xScreen("gte", "md")} {
                display: none;
            }
            ${({ searchInputFocus }: any) => searchInputFocus && `display: none;`};
        `,
    },
    accountMenuWrapper: {
        css: css`
            margin-right: 22px;

            ${xScreen("lte", "md")} {
                margin-right: 12px;
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
        `,
    },
    accountCartWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            ${xScreen("lte", "md")} {
                ${({ searchInputFocus }: any) => searchInputFocus && `width:0;max-width:0;height:0;visibility:hidden;`};
            }
        `,
    },
};

const mapStateToProps = (state: CustomAppState) => {
    const pageType: any = getCurrentPage(state).type;

    return {
        searchInputFocus: state.xHeader.searchInputFocus,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        isCheckoutPages:
            pageType === "CheckoutShippingPage" ||
            pageType === "CheckoutReviewAndSubmitPage" ||
            pageType === "OrderConfirmationPage" ||
            pageType === "RfqRequestQuotePage" ||
            pageType === "RfqConfirmationPage",
    };
};

const mapDispatchToProps = {
    setNavDrawerIsOpen,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper;

const LogoLinksSearch: FC<Props> = ({ id, searchInputFocus, isCheckoutPages, navDrawerIsOpen, setNavDrawerIsOpen }) => {
    const openDrawer = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: true });
    };

    const closeDrawer = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
    };
    return (
        <StyledWrapperX
            {...logoLinksSearchStyles.containerContentWrapper}
            {...({ isCheckoutPages } as any)}
            className="header_logo-links-search"
        >
            {!isCheckoutPages && (
                <StyledWrapperX searchInputFocus={searchInputFocus} {...logoLinksSearchStyles.searchMenuWrapper}>
                    <Hidden
                        above="md"
                        css={css`
                            @media print {
                                display: none;
                            }
                        `}
                    >
                        {navDrawerIsOpen ? (
                            <Clickable
                                onClick={closeDrawer}
                                {...{ searchInputFocus }}
                                {...logoLinksSearchStyles.menuTriggerButton}
                                data-test-selector="expandMobileMenu"
                                aria-label="Close"
                            >
                                <Icon
                                    size={14}
                                    src="Close"
                                    color="common.ui5"
                                    {...logoLinksSearchStyles.menuIcon}
                                    key="close-menu-icon"
                                />
                                <VisuallyHidden>{translate("close menu")}</VisuallyHidden>
                            </Clickable>
                        ) : (
                            <Clickable
                                onClick={openDrawer}
                                {...{ searchInputFocus }}
                                {...logoLinksSearchStyles.menuTriggerButton}
                                data-test-selector="expandMobileMenu"
                                aria-label="Open"
                            >
                                <Icon
                                    size={18}
                                    src="IconHeaderMenuInactive"
                                    color="common.ui5"
                                    {...logoLinksSearchStyles.menuIcon}
                                    key="open-menu-icon"
                                />
                                <VisuallyHidden>{translate("open menu")}</VisuallyHidden>
                            </Clickable>
                        )}
                    </Hidden>
                </StyledWrapperX>
            )}
            {/* mobile search button trigger comes here */}
            {!isCheckoutPages && (
                <StyledWrapperX searchInputFocus={searchInputFocus} {...logoLinksSearchStyles.mobileSearchWrapper}>
                    <Zone contentId={id} zoneName="searchInput" />
                </StyledWrapperX>
            )}
            <StyledWrapperX
                {...logoLinksSearchStyles.logoWrapper}
                searchInputFocus={searchInputFocus}
                {...{ isCheckoutPages }}
            >
                <Zone contentId={id} zoneName="LogoZone" />
            </StyledWrapperX>
            {!isCheckoutPages && (
                <StyledWrapperX
                    searchInputFocus={searchInputFocus}
                    {...logoLinksSearchStyles.navigationWrapper}
                    id="search-input"
                >
                    <Hidden
                        below="lg"
                        css={css`
                            display: flex;
                            align-items: center;
                            justify-content: flex-start;
                        `}
                    >
                        <StyledWrapperX {...logoLinksSearchStyles.mainNavigationWrapper}>
                            <Zone contentId={id} zoneName="mainNavigation" />
                        </StyledWrapperX>
                    </Hidden>
                    <StyledWrapperX searchInputFocus={searchInputFocus} {...logoLinksSearchStyles.searchWrapper}>
                        <Zone contentId={id} zoneName="searchInput" />
                    </StyledWrapperX>
                    <StyledWrapperX searchInputFocus={searchInputFocus} {...logoLinksSearchStyles.accountCartWrapper}>
                        <StyledWrapperX {...logoLinksSearchStyles.accountMenuWrapper}>
                            <Zone contentId={id} zoneName="accountMenu" />
                        </StyledWrapperX>
                        <StyledWrapperX {...logoLinksSearchStyles.cartLinkWrapper}>
                            <Zone contentId={id} zoneName="cartLink" />
                        </StyledWrapperX>
                    </StyledWrapperX>
                </StyledWrapperX>
            )}
        </StyledWrapperX>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
